import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  structuredData?: object;
  noindex?: boolean;
  productPrice?: string;
  productCurrency?: string;
  productAvailability?: string;
  productBrand?: string;
}

const baseUrl = 'https://store.braceesapori.it';

export const SEO = ({
  title,
  description,
  keywords,
  ogImage = '/logo.png',
  ogType = 'website',
  canonical,
  structuredData,
  noindex = false,
  productPrice,
  productCurrency = 'EUR',
  productAvailability,
  productBrand = 'Brace e Sapori',
}: SEOProps) => {
  const location = useLocation();
  const currentUrl = `${baseUrl}${location.pathname}${location.search}`;
  const canonicalUrl = canonical || currentUrl.split('?')[0]; // Rimuovi query params dal canonical

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    
    // Product specific meta tags
    if (productPrice) {
      updateMetaTag('product:price:amount', productPrice);
      updateMetaTag('product:price:currency', productCurrency);
    }
    if (productAvailability) {
      updateMetaTag('product:availability', productAvailability);
    }
    if (productBrand) {
      updateMetaTag('product:brand', productBrand);
    }

    // Open Graph tags
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:type', ogType, 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:image', `${baseUrl}${ogImage}`, 'property');
    updateMetaTag('og:locale', 'it_IT', 'property');
    updateMetaTag('og:site_name', 'Brace e Sapori', 'property');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', `${baseUrl}${ogImage}`);
    updateMetaTag('twitter:site', '@braceesapori');
    updateMetaTag('twitter:creator', '@braceesapori');

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // Structured Data (JSON-LD)
    // Rimuovi tutti gli script JSON-LD esistenti
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());
    
    if (structuredData) {
      // Supporta sia oggetto singolo che array di structured data
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      
      dataArray.forEach((data, index) => {
        const scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        scriptTag.textContent = JSON.stringify(data);
        document.head.appendChild(scriptTag);
      });
    }
  }, [title, description, keywords, ogImage, ogType, canonicalUrl, currentUrl, structuredData, noindex, productPrice, productCurrency, productAvailability, productBrand]);

  return null;
};

